/**
 * Phase F3 — Provider Notifications Center
 * -----------------------------------------
 * Reuses Phase 9 `/notifications/v2` for the provider role and exposes
 * a "Kind" filter focused on provider-relevant kinds:
 *   assignment.request, assignment.accepted, assignment.completed,
 *   compliance.reminder, distribution.generated.
 *
 * Endpoints used:
 *   GET  /api/notifications/v2?status=UNREAD|READ|ARCHIVED&notification_kind=...
 *   POST /api/notifications/{id}/read | unread | archive
 *
 * Pure UI. No backend logic modified.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Mail, MailOpen, Archive, CheckCheck, Inbox, Filter,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'UNREAD', label: 'Unread', Icon: Mail, color: theme.colors.primary },
  { key: 'READ', label: 'Read', Icon: MailOpen, color: theme.colors.accent.teal },
  { key: 'ARCHIVED', label: 'Archived', Icon: Archive, color: theme.colors.text.muted },
];

const PROVIDER_KINDS = [
  { key: '', label: 'All Kinds' },
  { key: 'assignment.request', label: 'Assignment Request' },
  { key: 'assignment.accepted', label: 'Assignment Accepted' },
  { key: 'assignment.completed', label: 'Assignment Completed' },
  { key: 'compliance.reminder', label: 'Compliance Reminder' },
  { key: 'distribution.generated', label: 'Distribution Generated' },
];

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, boxShadow: theme.colors.shadows.sm,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ProviderNotificationsCenter() {
  const [activeTab, setActiveTab] = useState('UNREAD');
  const [kindFilter, setKindFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ UNREAD: 0, READ: 0, ARCHIVED: 0 });
  const [supportedKinds, setSupportedKinds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: activeTab });
      if (kindFilter) params.set('notification_kind', kindFilter);
      const res = await axios.get(`${API}/notifications/v2?${params.toString()}`, auth);
      setRows(res.data?.notifications || []);
      setCounts(res.data?.counts || { UNREAD: 0, READ: 0, ARCHIVED: 0 });
      setSupportedKinds(res.data?.supported_kinds || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [activeTab, kindFilter, auth]);

  useEffect(() => { load(); }, [load]);

  const transition = async (id, action) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/notifications/${id}/${action}`, {}, auth);
      toast.success(`Marked ${action}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="provider-notifications-page" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 800, color: theme.colors.text.primary, margin: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Bell size={26} color={theme.colors.primary} />
          Notifications
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginTop: 4 }}>
          Assignment requests, acceptances, completions, compliance reminders, and distribution events.
        </p>
      </div>

      {/* Status tabs */}
      <div data-testid="provider-notif-tabs" style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {STATUS_TABS.map((t) => {
          const Icon = t.Icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`provider-notif-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', borderRadius: theme.radius.cta,
                border: `1px solid ${active ? t.color : theme.colors.borders}`,
                background: active ? `${t.color}15` : theme.colors.bg.card,
                color: active ? t.color : theme.colors.text.secondary,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}
            >
              <Icon size={16} /> {t.label}
              <span style={{
                marginLeft: 4, background: active ? t.color : theme.colors.bg.tertiary,
                color: active ? '#fff' : theme.colors.text.secondary,
                padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              }}>{counts[t.key] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Kind filter */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap',
      }}>
        <Filter size={14} color={theme.colors.text.muted} />
        <span style={{ fontSize: 12, color: theme.colors.text.muted, fontWeight: 600 }}>Kind</span>
        <select
          data-testid="provider-notif-kind-filter"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          style={{
            padding: '6px 10px', borderRadius: theme.radius.sm,
            border: `1px solid ${theme.colors.borders}`, background: theme.colors.bg.card,
            color: theme.colors.text.primary, fontSize: 12,
          }}
        >
          {PROVIDER_KINDS.map((k) => (
            <option key={k.key} value={k.key}>{k.label}</option>
          ))}
        </select>
        {supportedKinds.length > 0 && (
          <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
            ({supportedKinds.length} kinds supported for your role)
          </span>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div data-testid="provider-notif-loading" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.muted }}>
          Loading notifications…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="provider-notif-empty" style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.colors.text.secondary }}>
          <Inbox size={36} color={theme.colors.text.muted} style={{ margin: '0 auto 8px' }} />
          No {activeTab.toLowerCase()} notifications.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((n) => (
            <div
              key={n.id}
              data-testid={`provider-notif-row-${n.id}`}
              style={{
                ...cardStyle, padding: 16,
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
                borderLeft: `3px solid ${
                  n.status === 'UNREAD' ? theme.colors.primary :
                  n.status === 'READ' ? theme.colors.accent.teal :
                  theme.colors.text.muted
                }`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: 15 }}>
                    {n.title || 'Notification'}
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: 999,
                    background: theme.colors.bg.tertiary, color: theme.colors.text.secondary,
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>{n.notification_kind}</span>
                </div>
                {n.message && (
                  <div style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 6 }}>
                    {n.message}
                  </div>
                )}
                <div style={{ fontSize: 12, color: theme.colors.text.muted }}>
                  {fmtDate(n.created_at)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {n.status !== 'READ' && n.status !== 'ARCHIVED' && (
                  <button
                    data-testid={`provider-notif-read-${n.id}`}
                    disabled={busyId === n.id}
                    onClick={() => transition(n.id, 'read')}
                    style={{
                      padding: '6px 10px', borderRadius: theme.radius.sm,
                      border: `1px solid ${theme.colors.borders}`,
                      background: theme.colors.bg.card, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 12, fontWeight: 600, color: theme.colors.text.primary,
                    }}
                  >
                    <CheckCheck size={14} /> Read
                  </button>
                )}
                {n.status === 'READ' && (
                  <button
                    data-testid={`provider-notif-unread-${n.id}`}
                    disabled={busyId === n.id}
                    onClick={() => transition(n.id, 'unread')}
                    style={{
                      padding: '6px 10px', borderRadius: theme.radius.sm,
                      border: `1px solid ${theme.colors.borders}`,
                      background: theme.colors.bg.card, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 12, fontWeight: 600, color: theme.colors.text.primary,
                    }}
                  >
                    <Mail size={14} /> Unread
                  </button>
                )}
                {n.status !== 'ARCHIVED' && (
                  <button
                    data-testid={`provider-notif-archive-${n.id}`}
                    disabled={busyId === n.id}
                    onClick={() => transition(n.id, 'archive')}
                    style={{
                      padding: '6px 10px', borderRadius: theme.radius.sm,
                      border: `1px solid ${theme.colors.borders}`,
                      background: theme.colors.bg.card, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 12, fontWeight: 600, color: theme.colors.text.primary,
                    }}
                  >
                    <Archive size={14} /> Archive
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
